/**
 * Authentication utilities for making authenticated API requests
 * This ensures the Authorization header is sent with the token from localStorage
 * which is necessary for cross-origin requests in production where cookies may not work
 */

/**
 * Get the API URL - uses env variable or falls back to current origin at runtime
 * This is a function to ensure window.location.origin is accessed at runtime, not build time
 */
export const getApiUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  // Fallback to current origin - this handles production deployments
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
};

/**
 * Get auth headers including the Bearer token from localStorage
 * @returns {Object} Headers object with Authorization if token exists
 */
export const getAuthHeaders = () => {
  const headers = {};
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('auth_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return headers;
};

/**
 * Make an authenticated fetch request
 * Automatically includes credentials and Authorization header
 * @param {string} url - The URL to fetch (can be relative starting with /api)
 * @param {Object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>}
 */
export const authFetch = async (url, options = {}) => {
  const authHeaders = getAuthHeaders();
  const apiUrl = getApiUrl();
  
  const finalOptions = {
    ...options,
    credentials: 'include',
    headers: {
      ...authHeaders,
      ...options.headers
    }
  };
  
  // Prepend API_URL if the url is a relative path starting with /api
  const finalUrl = url.startsWith('/api') ? `${apiUrl}${url}` : url;
  
  return fetch(finalUrl, finalOptions);
};

/**
 * Make an authenticated JSON POST request
 * @param {string} url - The URL to post to
 * @param {Object} data - The data to send as JSON
 * @returns {Promise<Response>}
 */
export const authPost = async (url, data) => {
  return authFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });
};

/**
 * Make an authenticated GET request
 * @param {string} url - The URL to fetch
 * @returns {Promise<Response>}
 */
export const authGet = async (url) => {
  return authFetch(url, {
    method: 'GET'
  });
};

export default { getApiUrl, getAuthHeaders, authFetch, authPost, authGet };
